'use client'

import { useCallback, JSX, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { useQueryClient } from '@tanstack/react-query'
import { sum } from 'lodash'
import { EllipsisVertical } from 'lucide-react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  CurrencyAmount,
  List,
  ListItemProps,
  TextInput,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Dropdown } from '~/components/component-library/Dropdown'
import { Timestamp } from '~/components/component-library/Timestamp'
import { SignerIcon } from '~/components/smart-accounts/SignerIcon'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgUpdates } from '~/hooks/org'
import { useAddNewPasskey } from '~/hooks/useAddNewPasskey'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import {
  passkeyConfirmationHeaders,
  usePasskeyConfirmation,
} from '~/hooks/usePasskeyConfirmation'
import { usePasskeySmartAccounts } from '~/hooks/usePasskeySmartAccounts'
import { useServerRequests } from '~/hooks/useRequests'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { BackendPasskey, PasskeyConfirmationAction } from '~/types'
import { plural } from '~/utils/display'
import { captureException } from '~/utils/errors'
import {
  getPasskeyErrorDescription,
  startPasskeyAuthentication,
} from '~/utils/passkeys'
import { sendTrackingData } from '~/utils/tracking'

export const PasskeysEdit = (): JSX.Element => {
  const { passkeys } = useUserPasskeys()
  const { addNewPasskey } = useAddNewPasskey()

  const { setGlobalModalState } = useAuth()

  const closeGlobalModal = useCallback(() => {
    setGlobalModalState({ isOpen: false, content: null })
  }, [setGlobalModalState])

  return (
    <List
      label="Passkey signers"
      description="Log in and sign transactions using biometrics, a device password, or a PIN"
      items={[...passkeys]
        .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
        .map((passkey): ListItemProps => {
          return {
            key: passkey.id,
            image: <SignerIcon signer={passkey} />,
            content: passkey.name,
            meta: (
              <span>
                {passkey.lastVerifiedAt ? 'Last verified' : 'Added'}{' '}
                <Timestamp
                  timestamp={passkey.lastVerifiedAt ?? passkey.createdAt}
                  type="relative"
                  abbreviate
                  showTooltip={false}
                />
              </span>
            ),
            actionIcon: (
              <Dropdown
                items={[
                  {
                    content: 'Verify access...',
                    onClick: () =>
                      setGlobalModalState({
                        isOpen: true,
                        content: (
                          <VerifyPasskeyDialogContent
                            key={passkey.id}
                            passkey={passkey}
                            closeDialog={closeGlobalModal}
                          />
                        ),
                      }),
                  },
                  {
                    content: 'Rename...',
                    onClick: () =>
                      setGlobalModalState({
                        isOpen: true,
                        content: (
                          <RenamePasskeyDialogContent
                            key={passkey.id}
                            passkey={passkey}
                            closeDialog={closeGlobalModal}
                          />
                        ),
                      }),
                  },
                  {
                    content: 'Remove...',
                    onClick: () =>
                      setGlobalModalState({
                        isOpen: true,
                        content: (
                          <DeletePasskeyDialogContent
                            key={passkey.id}
                            passkey={passkey}
                            closeDialog={closeGlobalModal}
                          />
                        ),
                      }),
                  },
                ]}
              >
                <button type="button">
                  <EllipsisVertical size={16} />
                </button>
              </Dropdown>
            ),
          }
        })}
      cta={{
        label: 'Add passkey',
        onClick: () => {
          sendTrackingData('clickAddPasskey')
          addNewPasskey()
        },
      }}
    />
  )
}

const DeletePasskeyAcknowledgement = 'I UNDERSTAND'
const DeletePasskeySchema = z.object({
  acknowledgement: z.string().refine((v) => v === DeletePasskeyAcknowledgement),
})

export const DeletePasskeyDialogContent = ({
  passkey,
  closeDialog,
}: {
  passkey: BackendPasskey
  closeDialog: () => void
}): JSX.Element => {
  const { smartAccounts } = usePasskeySmartAccounts(passkey.id)
  // A passkey can sign for multiple workspaces with different enabled networks.
  const { balances } = useMultipleSmartAccountBalances(
    smartAccounts.map((a) => a.address),
    false,
    { allChains: true },
  )

  const signerInSmartAccountsTotalWorth = sum(
    Object.values(balances).map((item) => item.totalValue),
  )
  const numWorkspacesWithPasskey = new Set(
    smartAccounts.map((a) => a.orgId).filter(Boolean),
  ).size

  const { control, formState } = useForm({
    resolver: zodResolver(DeletePasskeySchema),
    defaultValues: { acknowledgement: '' },
  })

  const { serverDeleteRequest, isProcessing: isProcessingDelete } =
    useServerRequests()
  const { confirmWithPasskey, isConfirming } = usePasskeyConfirmation()

  const queryClient = useQueryClient()
  const { removePasskey } = useActiveOrgUpdates()

  const onDelete = async () => {
    const confirmationId = await confirmWithPasskey({
      action: PasskeyConfirmationAction.PasskeyArchive,
      resourceId: passkey.id,
    })
    if (!confirmationId) return

    const result = await serverDeleteRequest({
      path: `/api/passkey/${passkey.id}`,
      headers: passkeyConfirmationHeaders(confirmationId),
      successMessage: 'Passkey deleted',
      errorMessage: 'Failed to delete passkey',
    })

    if (result) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTH_USER] })
      removePasskey(passkey.id)
      closeDialog()
    }
  }

  return (
    <SplitsDialogContent
      id={'removePasskey'}
      title={`Remove ${passkey.name} passkey?`}
      variant="destructive"
      isLoading={isProcessingDelete || isConfirming}
      deleteButton={{
        text: 'Remove',
        variant: 'danger',
        onClick: onDelete,
        isDisabled: !formState.isValid,
        eventProperties: { passkeyId: passkey.id },
      }}
    >
      <p>
        You will no longer be able to sign transactions using this passkey. This
        action cannot be undone.
      </p>
      {smartAccounts.length > 0 && (
        <p>
          It is currently a signer on {smartAccounts.length}{' '}
          {plural('account', smartAccounts.length)}
          {numWorkspacesWithPasskey > 1 &&
            ` (across ${numWorkspacesWithPasskey} workspaces)`}
          , worth a combined value of{' '}
          <CurrencyAmount
            layout="inline"
            value={signerInSmartAccountsTotalWorth}
          />
          .
        </p>
      )}
      <TextInput
        label={`Type "I UNDERSTAND" to continue`}
        control={control}
        id="acknowledgement"
        inputName="acknowledgement"
        placeholder={DeletePasskeyAcknowledgement}
      />
    </SplitsDialogContent>
  )
}

const VerifyPasskeyDialogContent = ({
  passkey,
  closeDialog,
}: {
  passkey: BackendPasskey
  closeDialog: () => void
}): JSX.Element => {
  const { serverGetRequest, serverPostRequest } = useServerRequests()
  const { updatePasskey: updateOrgPasskey } = useActiveOrgUpdates()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const verifyPasskey = useCallback(async () => {
    setIsLoading(true)
    try {
      const options = await serverGetRequest<{
        options: PublicKeyCredentialRequestOptionsJSON
      }>({
        path: `/api/passkey/${passkey!.id}/test-options`,
      })

      if (options) {
        const attResp = await startPasskeyAuthentication(options.data.options, {
          onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
            if (isUnsupportedError) {
              toast.error('Browser does not support passkeys', {
                description:
                  error instanceof Error ? error.message : String(error),
              })
            } else if (isNotAllowedError) {
              toast.error("Couldn't verify passkey")
            } else {
              toast.error('Failed to verify passkey', {
                description: getPasskeyErrorDescription(error),
              })
              throw error
            }
          },
        })
        if (!attResp) return
        const response = await serverPostRequest<{ lastVerifiedAt: string }>({
          path: `/api/passkey/${passkey!.id}/test-verify`,
          params: {
            credentialId: attResp.id,
            authentication: JSON.stringify(attResp),
          },
          successMessage: `${passkey!.name} successfully verified`,
          errorMessage: `Failed to verify ${passkey!.name}`,
        })
        if (response && response.data.lastVerifiedAt) {
          updateOrgPasskey(
            passkey!.id,
            'lastVerifiedAt',
            response.data.lastVerifiedAt,
          )
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.AUTH_USER],
          })
          closeDialog()
        }
      }
    } catch (error) {
      captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [
    serverGetRequest,
    passkey,
    serverPostRequest,
    closeDialog,
    updateOrgPasskey,
    queryClient,
  ])

  return (
    <SplitsDialogContent
      id="passkeyVerification"
      variant="confirmation"
      title="Verify passkey"
      isLoading={isLoading}
      confirmButton={{
        text: 'Verify passkey',
        onClick: verifyPasskey,
      }}
    >
      <p>
        Verify you still have access to your {passkey.name} passkey. You last
        verified it on{' '}
        <Timestamp
          timestamp={passkey.lastVerifiedAt ?? passkey.createdAt}
          type="absolute"
          showTooltip={false}
        />
        .
      </p>
    </SplitsDialogContent>
  )
}

type PasskeyNameFormValues = {
  name: string
}

export const RenamePasskeyDialogContent = ({
  passkey,
  closeDialog,
  defaultName: defaultNameProp,
}: {
  passkey?: BackendPasskey
  closeDialog: () => void
  defaultName?: string
}): JSX.Element => {
  const queryClient = useQueryClient()
  const { serverPutRequest, isProcessing: isProcessingRename } =
    useServerRequests()

  const { updatePasskey: updateOrgPasskey } = useActiveOrgUpdates()

  const defaultName = passkey ? passkey.name : (defaultNameProp ?? '')
  const { control, handleSubmit, reset } = useForm<PasskeyNameFormValues>({
    defaultValues: {
      name: defaultName,
    },
  })

  const passkeyName = useWatch({ control, name: 'name' })

  const renamePasskey = useCallback(
    async (passkeyId: string, name: string) => {
      const result = await serverPutRequest<{
        authenticator: BackendPasskey
      }>({
        path: `/api/passkey/${passkeyId}`,
        params: {
          name,
        },
        errorMessage: 'Failed to rename passkey',
      })

      if (result?.data.authenticator) {
        updateOrgPasskey(passkeyId, 'name', result.data.authenticator.name)
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.AUTH_USER],
        })

        closeDialog()
        reset({ name: '' })
      }
    },
    [queryClient, serverPutRequest, updateOrgPasskey, closeDialog, reset],
  )

  const onSubmitName: SubmitHandler<PasskeyNameFormValues> = useCallback(
    async ({ name }) => {
      if (!passkey)
        throw new Error('passkey must be set when editing a passkey')

      await renamePasskey(passkey.id, name)
    },
    [passkey, renamePasskey],
  )

  return (
    <SplitsDialogContent
      id={'editPasskey'}
      title={'Rename passkey'}
      variant="cta"
      isLoading={isProcessingRename}
      actionButton={{
        text: 'Save',
        onClick: handleSubmit(onSubmitName),
        isDisabled: !passkeyName || passkeyName === defaultName,
        eventProperties: { passkeyId: passkey?.id },
      }}
    >
      <p>
        We recommend using the device or provider name (e.g. iCloud, Chrome,
        1Password, etc).
      </p>
      <form
        onSubmit={handleSubmit(onSubmitName)}
        autoComplete={'off'}
        spellCheck={'false'}
        className="space-y-4"
      >
        <TextInput
          label="Passkey name"
          control={control}
          inputName={'name'}
          placeholder={'Office MacBook'}
          autoFocus={true}
        />
      </form>
    </SplitsDialogContent>
  )
}
